import { hasFiles } from '@src/utils/dragDropUtils';
import { useCallback, useEffect, useRef, useState } from 'react';

type UseFileDragDropOptions = {
  onFilesDropped: (files: FileList) => void;
  isEnabled: boolean;
  onError?: (message: string) => void;
};

type UseFileDragDropReturn = {
  isDragging: boolean;
  dragHandlers: {
    onDragEnter: React.DragEventHandler<HTMLDivElement>;
    onDragLeave: React.DragEventHandler<HTMLDivElement>;
    onDragOver: React.DragEventHandler<HTMLDivElement>;
    onDrop: React.DragEventHandler<HTMLDivElement>;
  };
};

export const useFileDragDrop = ({
  onFilesDropped,
  isEnabled,
  onError,
}: UseFileDragDropOptions): UseFileDragDropReturn => {
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  const handleDragEnter: React.DragEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      if (!isEnabled || !hasFiles(e.dataTransfer)) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current += 1;
      setIsDragging(true);
    },
    [isEnabled],
  );

  const handleDragLeave: React.DragEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      if (!isEnabled) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();

      if (dragCounter.current > 0) {
        dragCounter.current -= 1;
      }

      if (dragCounter.current === 0) {
        setIsDragging(false);
      }
    },
    [isEnabled],
  );

  const handleDragOver: React.DragEventHandler<HTMLDivElement> = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop: React.DragEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setIsDragging(false);

      if (!isEnabled) {
        if (onError) {
          onError('ファイルのアップロードが許可されていません');
        }
        return;
      }

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        onFilesDropped(files);
      }
    },
    [isEnabled, onFilesDropped, onError],
  );

  useEffect(() => {
    const handleGlobalDragEnd = () => {
      if (isDragging) {
        setIsDragging(false);
        dragCounter.current = 0;
      }
    };

    const handleGlobalDrop = () => {
      setIsDragging(false);
      dragCounter.current = 0;
    };

    const handleMouseLeave = () => {
      if (isDragging) {
        setTimeout(() => {
          setIsDragging(false);
          dragCounter.current = 0;
        }, 100);
      }
    };

    document.addEventListener('dragend', handleGlobalDragEnd);
    document.addEventListener('drop', handleGlobalDrop);
    document.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('blur', handleGlobalDragEnd);

    return () => {
      document.removeEventListener('dragend', handleGlobalDragEnd);
      document.removeEventListener('drop', handleGlobalDrop);
      document.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('blur', handleGlobalDragEnd);
    };
  }, [isDragging]);

  return {
    isDragging,
    dragHandlers: {
      onDragEnter: handleDragEnter,
      onDragLeave: handleDragLeave,
      onDragOver: handleDragOver,
      onDrop: handleDrop,
    },
  };
};
